// Never-overlapping voice queue: priority reveal > intro/opening/finale > hint > reaction; stale reactions dropped.
import { SHOW_CONFIG } from './showConfig';
import { playEerie, playTextCue } from '@/spore/eerieAudio';

export function createVoiceQueue({ getEntry, ensureVoice, onStart = () => {}, onEnd = () => {}, onAudioEnd = () => {}, playOpts = {}, cfg = SHOW_CONFIG.voice, now = () => performance.now() }) {
  let queue = [];
  let current = null;

  function finish(token) {
    if (current !== token) return;
    current = null;
    onEnd(token.item);
    next();
  }

  function stopCurrent() {
    if (!current) return;
    const token = current;
    token.stopped = true;
    if (token.player) token.player.stop();
    current = null;
    onEnd(token.item);
  }

  async function next() {
    if (current) return;
    const t = now();
    queue = queue.filter((q) => !(q.kind === 'reaction' && t - q.at > cfg.staleMs));
    const item = queue.shift();
    if (!item) return;
    const token = { item, stopped: false, player: null };
    current = token;
    const entry = getEntry(item.id);
    const buffer = entry.buffer || (await ensureVoice(item.id, cfg.onDemandTimeoutMs));
    if (token.stopped || current !== token) return;
    let durationMs;
    if (buffer) {
      token.player = playEerie(buffer, playOpts);
      durationMs = token.player.duration * 1000;
      token.player.ended.then(() => {
        if (current === token) onAudioEnd(token.item);
        setTimeout(() => finish(token), cfg.lineGapMs ?? 450); // breathing gap before the next line
      });
      setTimeout(() => finish(token), durationMs + 2500);
    } else {
      playTextCue();
      durationMs = Math.max(cfg.textOnlyMinMs, entry.text.length * cfg.textOnlyMsPerChar + 1200);
      setTimeout(() => finish(token), durationMs);
    }
    token.mode = buffer ? 'voice' : 'text';
    onStart({ id: item.id, kind: item.kind, text: entry.text, source: entry.source, mode: token.mode, durationMs, startedAt: now() });
  }

  return {
    enqueue(cue) {
      const item = { ...cue, at: now() };
      if (item.priority >= 3) queue = queue.filter((q) => q.priority >= 3);
      if (item.kind === 'reaction' || item.kind === 'hint') queue = queue.filter((q) => q.kind !== item.kind);
      if (current && item.priority >= 3 && current.item.priority < item.priority) stopCurrent();
      queue.push(item);
      queue.sort((a, b) => b.priority - a.priority || a.at - b.at);
      next();
    },
    clear() {
      queue = [];
      stopCurrent();
    },
    isBusy: () => !!current || queue.length > 0,
    getState: () => ({
      playing: current ? { id: current.item.id, kind: current.item.kind, mode: current.mode || 'loading' } : null,
      queued: queue.map((q) => q.id),
    }),
  };
}
